/**
 * @file Product law: no card numbers, ever. The PAN-pattern rejection as pure
 * functions and the zod refinements built on it. It lives apart from payments.ts so
 * ledger.ts (which payments.ts imports) can use the same rule without an import
 * cycle; payments.ts re-exports the public names so the kernel index is unchanged.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Product law — no card numbers, ever
// ---------------------------------------------------------------------------

/**
 * The smallest run of digits that is treated as a payment-card number.
 *
 * 13 is the shortest real PAN (Visa's original length); 12 and below are ordinary
 * numbers a person types — a check number, a unit number, a date.
 */
export const PAN_DIGIT_RUN_MIN = 13;

/**
 * The longest run that is treated as a payment-card number.
 *
 * 19 is the longest PAN the industry issues. A run of 20 or more digits is NOT a
 * card number, and rejecting it would be this rule pretending to be a general
 * "no long numbers" ban — which it is not.
 */
export const PAN_DIGIT_RUN_MAX = 19;

/**
 * The message a rejected field carries.
 *
 * It names the shape that was refused and never the value: an error that echoed the
 * input would put the very thing this rule exists to keep out into a log, a toast
 * and a Sentry event. `_factory/conventions.md`: the Restricted data class stays
 * EMPTY — and "empty" includes the error path.
 */
export const PAN_REJECTION_MESSAGE =
  "cannot contain a payment card or bank account number — remove the run of 13 to " +
  "19 digits and use words or a short reference instead";

/** Where a suspicious digit run was found. Never carries the digits themselves. */
export interface PanLikeDigitRun {
  /** Index of the first digit in the input. */
  readonly start: number;
  /** Index just past the last digit — the input's own coordinates. */
  readonly end: number;
  /** How many digits the run contained, 13–19. */
  readonly digits: number;
}

/** A digit, or one of the separators a card number is written with. */
const DIGIT_OR_SEPARATOR = /[0-9 -]/;

function isDigit(character: string | undefined): boolean {
  return character !== undefined && character >= "0" && character <= "9";
}

/**
 * **The PAN-pattern rejection, as one pure function.**
 *
 * `_factory/conventions.md` §Product law requires it: manual-payment fields reject
 * PAN-like patterns, so no card number, SSN or bank account number is ever written
 * to our database. It protects the ONLY data class the product promises to keep
 * empty, and it protects the person typing — a landlord pasting a tenant's card
 * number into a memo is how a landlord becomes a cardholder-data processor.
 *
 * The rule: a maximal run of digits **and the spaces and dashes a card number is
 * written with** whose digit count is between 13 and 19 inclusive is refused.
 * Separators count because someone WILL type `4111 1111 1111 1111` or
 * `4111-1111-1111-1111`, and a rule that only caught unbroken digits would miss
 * exactly the person it was written for. Trailing separators are not part of the
 * run, so the coordinates point at the last digit.
 *
 * Two consequences are deliberate:
 *
 *   * **20 digits or more is accepted.** The rule is about payment cards, not about
 *     long numbers; widening it would be inventing a different policy, and the
 *     boundaries (`12 ok, 13 rejected, 19 rejected, 20 ok`) are pinned by tests.
 *   * **It is fail-closed, so an honest sentence can trip it.** A memo reading
 *     `2026-01-01 2026-01-31` is a run of sixteen digits once separators are
 *     crossed. That is the safe direction — the landlord reworks the wording, and
 *     nothing sensitive is stored — and it is the reason the message says what to
 *     do rather than only what failed.
 *
 * Returns the run, or `null` when the text is clean. The caller must NOT put the
 * text in the response; see `PAN_REJECTION_MESSAGE`.
 */
export function findPanLikeDigitRun(value: string): PanLikeDigitRun | null {
  let index = 0;
  while (index < value.length) {
    if (!isDigit(value[index])) {
      index += 1;
      continue;
    }

    // Walk the whole run — digits and the separators between them — counting the
    // digits and remembering where the last one was.
    let scan = index;
    let digits = 0;
    let lastDigit = index;
    while (scan < value.length && DIGIT_OR_SEPARATOR.test(value[scan] ?? "")) {
      if (isDigit(value[scan])) {
        digits += 1;
        lastDigit = scan;
      }
      scan += 1;
    }

    if (digits >= PAN_DIGIT_RUN_MIN && digits <= PAN_DIGIT_RUN_MAX) {
      return { start: index, end: lastDigit + 1, digits };
    }
    // A clean run, or one too long to be a card number. Either way the run is
    // consumed whole, so a 20-digit run contributes no 13–19 digit sub-run.
    index = scan;
  }
  return null;
}

/** Does this free text look like it contains a card number? The boolean half. */
export function hasPanLikeDigitRun(value: string): boolean {
  return findPanLikeDigitRun(value) !== null;
}

/**
 * The refinement every free-text manual-payment field carries.
 *
 * Applied to `memo` and `reference` below, and to any field added later — a new
 * field that forgets it would be a hole in product law, which is why it is a named
 * helper rather than an inline `.refine` copied per field.
 */
export function panFree(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .refine((value) => !hasPanLikeDigitRun(value), { message: PAN_REJECTION_MESSAGE });
}

/**
 * Optional PAN-free text, stored as `null` when absent or blank — the same
 * normalisation `ledger.ts` uses, so "no memo" has exactly one representation.
 */
export function optionalPanFree(max: number) {
  return panFree(max)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * Required PAN-free text — same as optionalPanFree but the field must be present
 * and non-empty. Used for fields where the content cannot be null.
 */
export function requiredPanFree(max: number) {
  return panFree(max).refine((value) => value.length > 0, { message: "is required" });
}
